/**
 * Reads the payload out of a signed Wix app instance, in the browser.
 *
 * An instance is `${signature}.${base64UrlPayload}` (lib/orion-instance.ts mints ours), and the
 * payload is plain JSON — `metaSiteId`, `appDefId`, `signDate`, and on the platform-minted ones
 * `expirationDate`. Two callers want different fields out of it (instance-auth wants the expiry,
 * sentry-identity wants the metasite), so the decode lives here rather than being written twice.
 *
 * NOT a validation: the signature is never checked here and cannot be — the secret is server-side.
 * Everything this returns is a claim the browser was handed, fine for deciding when to re-mint or
 * which account to label an error report with, and never sufficient to authorize anything.
 */

function base64UrlDecode(segment: string): string {
  const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  return atob(b64 + pad);
}

export interface WixInstancePayload {
  metaSiteId?: string;
  appDefId?: string;
  signDate?: string;
  expirationDate?: string;
}

/** The decoded payload, or null when the string is not an instance / the payload is unreadable. */
export function decodeInstancePayload(instance: string): WixInstancePayload | null {
  const payloadSegment = instance.split('.')[1];
  if (!payloadSegment) return null;
  try {
    const payload = JSON.parse(base64UrlDecode(payloadSegment)) as unknown;
    return payload !== null && typeof payload === 'object' ? (payload as WixInstancePayload) : null;
  } catch {
    return null;
  }
}
